import { DrawerDetalleObra } from '@/components/dominio/DrawerDetalleObra';
import { TituloOrdenable } from '@/components/dominio/TituloOrdenable';
import { TreemapCategorias } from '@/components/dominio/TreemapCategorias';
import { FiltroCategorias } from '@/components/filtros/FiltroCategorias';
import { TablaExpandible } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAreaCosteoResumen,
  useCostoPorCategoria,
  usePresupuestoPartidas,
} from '@/hooks/useAreaCosteo';
import { useFiltrosGlobales } from '@/lib/FiltrosGlobalesContext';
import { type OrdenColumna, ordenarHermanos, siguienteOrden } from '@/lib/ordenColumna';
import type { PresupuestoTarea, ResumenAreaCosteo } from '@adelante/shared';
import { formatearCRC } from '@adelante/shared';
import { CaretDown, CaretRight, CircleNotch, X } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

/**
 * Vista Costos Detallados: árbol jerárquico de 7 niveles (replica el reporte
 * de Power BI viejo "Analisis Obra").
 *
 * Jerarquía completa:
 *   Área Costeo → Proyecto → Bloque → Obra → Partida → Categoría → Item
 *
 * Cada fila del recordset SQL es la combinación más granular (item). El
 * frontend agrupa recursivamente. Click en una Obra abre Drawer Detalle.
 *
 * Comparte el endpoint `/api/area-costeo-analisis` con AreaCosteoPantalla
 * pero acá lo presentamos sin el treemap, ocupando todo el ancho.
 */

interface NodoArbol {
  key: string;
  label: string;
  totalCostoLcy: number;
  totalQuantity: number;
  hijos: NodoArbol[];
  nivel: number;
  /** worksNo asociado al nodo (si nivel = Obra). Permite abrir drawer. */
  worksNoSiObra?: string;
  /** Para ordenamiento: valor crudo del nivel (sin enriquecer label). */
  valorCrudo: string;
  /** True si este nivel debe ordenarse natural (numérico) en vez de por costo. */
  ordenNatural: boolean;
  /**
   * Presupuesto correspondiente a este nodo:
   * - Niveles 0-3 (Área/Proyecto/Bloque/Obra): presupuesto total de las obras.
   * - Nivel 4 (Tipo Costo): componente Directo o Indirecto de la obra.
   * - Nivel 5 (Partida): presupuesto de esa partida (works_no + task_no).
   * - Niveles 6-7 (Categoría/Item) y partidas sin presupuesto: `null` (blanco).
   */
  presupuesto: number | null;
}

/** Índices de nivel en NIVELES que muestran presupuesto. */
const NIVEL_OBRA = 3;
const NIVEL_TIPO_COSTO = 4;
const NIVEL_PARTIDA = 5;

/**
 * Definición de un nivel del árbol. `field` lee directo de la fila; `derivado`
 * computa un valor sintético (ej. Tipo Costo Directo/Indirecto desde task_no).
 */
interface NivelDef {
  etiqueta: string;
  field?: keyof ResumenAreaCosteo;
  derivado?: (f: ResumenAreaCosteo) => string;
  /** Ordenar hijos de este nivel natural (numérico asc) en vez de costo desc. */
  ordenNatural?: boolean;
}

/**
 * Indica si una partida es indirecta (task_no empieza con "CI": CI.PV, CI.SC).
 */
function esPartidaIndirecta(taskNo: string | null): boolean {
  return !!taskNo && taskNo.toUpperCase().startsWith('CI');
}

/** Normaliza para búsqueda: minúsculas + sin acentos (para que "cemento" matchee "Cemento"). */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

const NIVELES: NivelDef[] = [
  { field: 'areaCosteo', etiqueta: 'Área de Costeo' },
  { field: 'bloque', etiqueta: 'Proyecto' },
  { field: 'subBloque', etiqueta: 'Bloque' },
  { field: 'worksNo', etiqueta: 'Obra' },
  // Nivel sintético: Tipo Costo (Directo / Indirecto) derivado de task_no
  {
    etiqueta: 'Tipo Costo',
    derivado: (f) => (esPartidaIndirecta(f.taskNo) ? '2 · Costo Indirecto' : '1 · Costo Directo'),
  },
  { field: 'taskNo', etiqueta: 'Partida', ordenNatural: true },
  { field: 'itemCategoryCode', etiqueta: 'Categoría' },
  { field: 'descripcionItem', etiqueta: 'Item' },
];

/**
 * Presupuesto preprocesado para alimentar el árbol en sus distintos niveles.
 * - `obra`: por works_no → { total, costo, indirecto } (ya con el filtro de
 *   Excluir Indirectos aplicado en `total`).
 * - `partida`: por `${worksNo}|${taskNo}` → presupuesto de esa partida.
 */
interface DatosPresupuesto {
  obra: Map<string, { total: number; costo: number; indirecto: number }>;
  partida: Map<string, number>;
}

/** Llave del mapa de presupuesto por partida. */
function llavePartida(worksNo: string, taskNo: string): string {
  return `${worksNo}|${taskNo}`;
}

function construirArbol(
  filas: ResumenAreaCosteo[],
  // `null` cuando hay filtro de categoría activo: el presupuesto es por obra/
  // partida completa y compararlo contra un costo filtrado por categoría sería
  // engañoso.
  datosPres: DatosPresupuesto | null,
  // Niveles a usar. Por defecto NIVELES; el checkbox "Sin Categoría" pasa la
  // lista sin el nivel itemCategoryCode (Partida → Item directo).
  niveles: NivelDef[] = NIVELES,
): NodoArbol[] {
  function recurse(
    filasNivel: ResumenAreaCosteo[],
    nivel: number,
    pathParent: string,
  ): NodoArbol[] {
    const nivelDef = niveles[nivel];
    if (!nivelDef) return [];

    const valorDe = (f: ResumenAreaCosteo): string => {
      if (nivelDef.derivado) return nivelDef.derivado(f);
      const raw = nivelDef.field ? f[nivelDef.field] : null;
      return raw == null ? '(—)' : String(raw);
    };

    const grupos = new Map<string, ResumenAreaCosteo[]>();
    for (const fila of filasNivel) {
      const valor = valorDe(fila);
      if (!grupos.has(valor)) grupos.set(valor, []);
      grupos.get(valor)!.push(fila);
    }

    const nodos: NodoArbol[] = [];
    for (const [valor, filasGrupo] of grupos.entries()) {
      const key = `${pathParent}/${valor}`;
      const hijos = recurse(filasGrupo, nivel + 1, key);
      const primera = filasGrupo[0]!;
      const enriquecerLabel = (): string => {
        if (nivelDef.field === 'worksNo') {
          const display = primera.proyectoDisplay;
          return display && display !== valor ? `${valor} — ${display}` : valor;
        }
        if (nivelDef.field === 'taskNo') {
          const display = primera.tareaDisplay;
          return display && display !== valor ? `${valor} — ${display}` : valor;
        }
        // Para "Tipo Costo", quitar el prefijo de orden "1 · " / "2 · "
        if (nivelDef.derivado) return valor.replace(/^\d+ · /, '');
        return valor;
      };
      // Presupuesto según el nivel del nodo:
      let presupuesto: number | null = null;
      if (datosPres) {
        if (nivel <= NIVEL_OBRA) {
          // Suma del presupuesto total de cada obra distinta bajo el nodo.
          const obrasUnicas = new Set(filasGrupo.map((f) => f.worksNo));
          let suma = 0;
          for (const w of obrasUnicas) suma += datosPres.obra.get(w)?.total ?? 0;
          presupuesto = suma;
        } else if (nivel === NIVEL_TIPO_COSTO) {
          // Componente Directo (Cost) o Indirecto (Indirect Cost) de la obra.
          // `valor` viene como "1 · Costo Directo" / "2 · Costo Indirecto".
          const esIndirecto = valor.startsWith('2');
          const obrasUnicas = new Set(filasGrupo.map((f) => f.worksNo));
          let suma = 0;
          for (const w of obrasUnicas) {
            const o = datosPres.obra.get(w);
            if (o) suma += esIndirecto ? o.indirecto : o.costo;
          }
          presupuesto = suma;
        } else if (nivel === NIVEL_PARTIDA) {
          // Presupuesto de la partida (works_no + task_no). `null` si no fue
          // presupuestada (≈20% de las partidas con costo no tienen presupuesto).
          const w = primera.worksNo;
          const k = llavePartida(w, valor);
          presupuesto = datosPres.partida.has(k) ? datosPres.partida.get(k)! : null;
        }
      }

      nodos.push({
        key,
        label: enriquecerLabel(),
        valorCrudo: valor,
        ordenNatural: !!nivelDef.ordenNatural,
        totalCostoLcy: filasGrupo.reduce((acc, f) => acc + f.totalCostoLcy, 0),
        totalQuantity: filasGrupo.reduce((acc, f) => acc + f.totalQuantity, 0),
        hijos,
        nivel,
        worksNoSiObra: nivelDef.field === 'worksNo' ? valor : undefined,
        presupuesto,
      });
    }

    // Orden: natural (numérico asc) para Partidas y Tipo Costo;
    // costo descendente para el resto.
    if (nodos[0]?.ordenNatural || nivelDef.derivado) {
      nodos.sort((a, b) =>
        a.valorCrudo.localeCompare(b.valorCrudo, 'es', { numeric: true, sensitivity: 'base' }),
      );
    } else {
      nodos.sort((a, b) => b.totalCostoLcy - a.totalCostoLcy);
    }
    return nodos;
  }
  return recurse(filas, 0, '');
}

/**
 * Construye los mapas de presupuesto (por obra y por partida) a partir de las
 * filas por partida del endpoint, aplicando el toggle "Excluir Indirectos":
 * cuando se excluyen indirectos solo cuenta el presupuesto de Costo directo;
 * de lo contrario suma Costo + Indirecto (la venta nunca entra: es ingreso).
 */
function mapaPresupuesto(filas: PresupuestoTarea[], excluirIndirectos: boolean): DatosPresupuesto {
  const obra = new Map<string, { total: number; costo: number; indirecto: number }>();
  const partida = new Map<string, number>();
  for (const p of filas) {
    const valorTarea = p.presupuestoCosto + (excluirIndirectos ? 0 : p.presupuestoIndirecto);
    // Por partida (acumula por si llegaran filas repetidas del mismo task_no).
    const k = llavePartida(p.worksNo, p.taskNo);
    partida.set(k, (partida.get(k) ?? 0) + valorTarea);
    // Por obra: total + componentes Cost / Indirect Cost.
    const acc = obra.get(p.worksNo) ?? { total: 0, costo: 0, indirecto: 0 };
    acc.total += valorTarea;
    acc.costo += p.presupuestoCosto;
    acc.indirecto += p.presupuestoIndirecto;
    obra.set(p.worksNo, acc);
  }
  return { obra, partida };
}

export function CostosDetalladosPantalla() {
  const { filtros } = useFiltrosGlobales();
  const { data, isLoading, error } = useAreaCosteoResumen();
  const { data: presupuestoData } = usePresupuestoPartidas();
  const { data: categoriasData } = useCostoPorCategoria();
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());
  const [orden, setOrden] = useState<OrdenColumna | null>(null);
  const [obraDrawer, setObraDrawer] = useState<string | null>(null);

  // Filtro por categoría (dos controles ortogonales):
  // - categoriasVisibles: dropdown multi-select (qué categorías existen).
  //   null = todas. Define el subconjunto del treemap (recalcula %).
  // - categoriaEnfocada: click en el treemap (las demás en gris + filtra árbol).
  const [categoriasVisibles, setCategoriasVisibles] = useState<Set<string> | null>(null);
  const [categoriaEnfocada, setCategoriaEnfocada] = useState<string | null>(null);
  // Checkbox: si está activo, el árbol omite el nivel "Categoría" y va de
  // Partida directo al Item/Cuenta (insumo).
  const [excluirCategoria, setExcluirCategoria] = useState(false);
  // Búsqueda libre por descripción de producto/cuenta (Dim.Codigo) o categoría.
  // Filtra las filas hoja y reconstruye el árbol solo con las ramas que matchean.
  const [busqueda, setBusqueda] = useState('');

  const todasCategorias = categoriasData?.data ?? [];

  // Enfoque efectivo: ignora una categoría enfocada que ya no esté visible.
  const enfoqueEfectivo =
    categoriaEnfocada != null &&
    (categoriasVisibles === null || categoriasVisibles.has(categoriaEnfocada))
      ? categoriaEnfocada
      : null;

  const hayFiltroCategoria = categoriasVisibles !== null || enfoqueEfectivo !== null;

  const toggleCategoriaVisible = (code: string) => {
    setCategoriasVisibles((prev) => {
      const base = prev ?? new Set(todasCategorias.map((c) => c.itemCategoryCode));
      const next = new Set(base);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      // Si quedaron todas, normalizamos a null (sin filtro).
      if (next.size === todasCategorias.length) return null;
      return next;
    });
  };
  const enfocarCategoria = (code: string) =>
    setCategoriaEnfocada((prev) => (prev === code ? null : code));
  const limpiarFiltroCategoria = () => {
    setCategoriasVisibles(null);
    setCategoriaEnfocada(null);
  };

  const datosPresupuesto = useMemo(
    () => mapaPresupuesto(presupuestoData?.data ?? [], filtros.excluirIndirectos === true),
    [presupuestoData, filtros.excluirIndirectos],
  );

  const busquedaNorm = normalizar(busqueda);
  const hayBusqueda = busquedaNorm.length > 0;

  const arbol = useMemo(() => {
    const filas = data?.data ?? [];
    let filasFiltradas = hayFiltroCategoria
      ? filas.filter((f) => {
          if (categoriasVisibles && !categoriasVisibles.has(f.itemCategoryCode)) return false;
          if (enfoqueEfectivo && f.itemCategoryCode !== enfoqueEfectivo) return false;
          return true;
        })
      : filas;
    // Búsqueda libre: matchea contra la descripción del item (producto/cuenta) y
    // su categoría. Reconstruye el árbol solo con las ramas que contienen un hit.
    if (hayBusqueda) {
      filasFiltradas = filasFiltradas.filter(
        (f) =>
          normalizar(f.descripcionItem ?? '').includes(busquedaNorm) ||
          normalizar(f.itemCategoryCode).includes(busquedaNorm),
      );
    }
    // Niveles: opcionalmente sin el nivel Categoría (Partida → Item directo).
    const niveles = excluirCategoria
      ? NIVELES.filter((n) => n.field !== 'itemCategoryCode')
      : NIVELES;
    // Con filtro de categoría o búsqueda el presupuesto (por obra/partida completa)
    // no es comparable contra un costo filtrado, así que se oculta.
    const ocultarPresupuesto = hayFiltroCategoria || hayBusqueda;
    return construirArbol(filasFiltradas, ocultarPresupuesto ? null : datosPresupuesto, niveles);
  }, [
    data,
    datosPresupuesto,
    hayFiltroCategoria,
    categoriasVisibles,
    enfoqueEfectivo,
    excluirCategoria,
    hayBusqueda,
    busquedaNorm,
  ]);

  // Cuando hay búsqueda activa, expandir todo el árbol resultante para que los
  // hits (en niveles hoja) sean visibles sin tener que abrir manualmente.
  const todasLasKeys = useMemo(() => {
    const keys = new Set<string>();
    const visitar = (nodos: NodoArbol[]) => {
      for (const n of nodos) {
        if (n.hijos.length > 0) {
          keys.add(n.key);
          visitar(n.hijos);
        }
      }
    };
    visitar(arbol);
    return keys;
  }, [arbol]);

  const expandidasEfectivas = hayBusqueda ? todasLasKeys : expandidas;

  const alOrdenar = (idx: number) => setOrden((actual) => siguienteOrden(actual, idx));
  const toggle = (key: string) => {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandirTodos = () => {
    const todos = new Set<string>();
    const visitar = (nodos: NodoArbol[]) => {
      for (const n of nodos) {
        if (n.hijos.length > 0) {
          todos.add(n.key);
          visitar(n.hijos);
        }
      }
    };
    visitar(arbol);
    setExpandidas(todos);
  };
  const colapsarTodos = () => setExpandidas(new Set());

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
        Cargando…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const acciones = (
    <div className="flex items-center gap-2 text-xs mr-2">
      <div className="relative">
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar producto o cuenta…"
          className="h-7 w-56 rounded border border-zinc-300 pl-2 pr-6 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          title="Filtra por descripción del producto/cuenta (Dim.Codigo) o categoría"
        />
        {busqueda && (
          <button
            type="button"
            onClick={() => setBusqueda('')}
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-zinc-100 hover:text-foreground"
            title="Limpiar búsqueda"
            aria-label="Limpiar búsqueda"
          >
            <X className="h-3 w-3" weight="bold" />
          </button>
        )}
      </div>
      <span className="text-zinc-300">|</span>
      <label
        className="flex items-center gap-1.5 cursor-pointer select-none"
        title="Oculta el nivel Categoría: la Partida se desglosa directo en Item/Cuenta (insumo)"
      >
        <input
          type="checkbox"
          checked={excluirCategoria}
          onChange={(e) => setExcluirCategoria(e.target.checked)}
          className="h-3.5 w-3.5"
        />
        Sin Categoría
      </label>
      <span className="text-zinc-300">|</span>
      <button type="button" onClick={expandirTodos} className="text-blue-600 hover:underline">
        Expandir todo
      </button>
      <span className="text-zinc-300">|</span>
      <button type="button" onClick={colapsarTodos} className="text-blue-600 hover:underline">
        Colapsar
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Costos Detallados</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Árbol jerárquico 8 niveles: Área → Proyecto → Bloque → Obra → Tipo Costo → Partida →
          Categoría → Item. La columna Presupuesto y su variación llegan hasta el nivel Partida (las
          partidas sin presupuesto cargado quedan en blanco). Usá el selector de Categorías o hacé
          click en el gráfico para filtrar por categoría.
        </p>
      </div>

      <TreemapCategorias
        titulo="Gasto por Categoría"
        seleccion={categoriasVisibles}
        enfocada={enfoqueEfectivo}
        onEnfocar={enfocarCategoria}
        acciones={
          <div className="flex items-center gap-2">
            {hayFiltroCategoria && (
              <button
                type="button"
                onClick={limpiarFiltroCategoria}
                className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
              >
                <X className="h-3 w-3" weight="bold" />
                Mostrar todas
              </button>
            )}
            <FiltroCategorias
              categorias={todasCategorias}
              seleccion={categoriasVisibles}
              onToggle={toggleCategoriaVisible}
              onTodas={() => setCategoriasVisibles(null)}
              onNinguna={() => setCategoriasVisibles(new Set())}
            />
          </div>
        }
      />

      {enfoqueEfectivo && (
        <p className="text-xs text-muted-foreground">
          Mostrando solo la categoría <span className="font-medium">{enfoqueEfectivo}</span> en la
          tabla. La columna Presupuesto se oculta porque el presupuesto es por obra completa.
        </p>
      )}

      <TablaExpandible titulo="Costos Detallados" acciones={acciones}>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Detalle por jerarquía
              {hayFiltroCategoria && (
                <span className="ml-2 text-xs font-normal text-blue-600">
                  · filtrado por categoría
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Jerarquía</TableHead>
                  <TableHead className="text-right text-xs w-36">
                    <TituloOrdenable titulo="Costo" idx={0} orden={orden} onOrdenar={alOrdenar} />
                  </TableHead>
                  <TableHead className="text-right text-xs w-36">
                    <TituloOrdenable
                      titulo="Presupuesto"
                      idx={1}
                      orden={orden}
                      onOrdenar={alOrdenar}
                    />
                  </TableHead>
                  <TableHead className="text-right text-xs w-36">
                    <TituloOrdenable
                      titulo="Variación"
                      idx={2}
                      orden={orden}
                      onOrdenar={alOrdenar}
                    />
                  </TableHead>
                  <TableHead className="text-right text-xs w-24">
                    <TituloOrdenable titulo="Cant" idx={3} orden={orden} onOrdenar={alOrdenar} />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {arbol.length === 0 && hayBusqueda ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-xs text-muted-foreground py-6"
                    >
                      Sin resultados para “{busqueda}”.
                    </TableCell>
                  </TableRow>
                ) : (
                  ordenarHermanos(arbol, orden, valoresNodo).map((nodo) => (
                    <FilaArbol
                      key={nodo.key}
                      nodo={nodo}
                      orden={orden}
                      expandidas={expandidasEfectivas}
                      onToggle={toggle}
                      onAbrirObra={(w) => setObraDrawer(w)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </TablaExpandible>

      <DrawerDetalleObra worksNo={obraDrawer} onClose={() => setObraDrawer(null)} />
    </div>
  );
}

/** Valores de las 4 columnas numéricas (Costo, Presupuesto, Variación, Cant) para ordenar. */
const valoresNodo = (n: NodoArbol): (number | null)[] => [
  n.totalCostoLcy || null,
  n.presupuesto,
  n.presupuesto != null ? n.totalCostoLcy - n.presupuesto : null,
  n.totalQuantity || null,
];

function FilaArbol({
  nodo,
  orden,
  expandidas,
  onToggle,
  onAbrirObra,
}: {
  nodo: NodoArbol;
  orden: OrdenColumna | null;
  expandidas: Set<string>;
  onToggle: (k: string) => void;
  onAbrirObra: (worksNo: string) => void;
}) {
  const tieneHijos = nodo.hijos.length > 0;
  const abierto = expandidas.has(nodo.key);
  const indent = nodo.nivel * 16;

  const bgPorNivel = [
    'bg-zinc-100 font-semibold', // 0 Área
    'bg-zinc-50 font-medium', // 1 Proyecto
    'bg-white font-medium', // 2 Bloque
    'bg-white', // 3 Obra
    'bg-amber-50/40 font-medium', // 4 Tipo Costo (Directo/Indirecto)
    'bg-white text-muted-foreground', // 5 Partida
    'bg-white text-muted-foreground', // 6 Categoría
    'bg-white text-muted-foreground', // 7 Item
  ];

  // Click en una fila de nivel Obra (nivel 3) abre el drawer detalle.
  const esObra = nodo.worksNoSiObra !== undefined;
  const onRowClick = () => {
    if (esObra) {
      onAbrirObra(nodo.worksNoSiObra!);
    } else if (tieneHijos) {
      onToggle(nodo.key);
    }
  };

  return (
    <>
      <TableRow
        className={`${bgPorNivel[nodo.nivel] ?? ''} ${
          esObra
            ? 'cursor-pointer hover:bg-blue-50'
            : tieneHijos
              ? 'cursor-pointer hover:bg-zinc-200'
              : ''
        }`}
        onClick={tieneHijos || esObra ? onRowClick : undefined}
      >
        <TableCell className="text-xs">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${indent}px` }}>
            {tieneHijos ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(nodo.key);
                }}
                className="hover:bg-zinc-200 rounded p-0.5"
                title={abierto ? 'Colapsar' : 'Expandir'}
              >
                {abierto ? (
                  <CaretDown className="h-3 w-3" weight="bold" />
                ) : (
                  <CaretRight className="h-3 w-3" weight="bold" />
                )}
              </button>
            ) : (
              <span className="w-3 inline-block" />
            )}
            <span className="truncate max-w-[500px]">
              {nodo.label}
              {esObra && <span className="ml-2 text-[10px] text-blue-600">(click → detalle)</span>}
            </span>
          </div>
        </TableCell>
        <TableCell className="text-right text-xs tabular-nums">
          {formatearCRC(nodo.totalCostoLcy)}
        </TableCell>
        <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
          {nodo.presupuesto != null ? formatearCRC(nodo.presupuesto) : ''}
        </TableCell>
        <TableCell className="text-right text-xs tabular-nums">
          {(() => {
            if (nodo.presupuesto == null) return '';
            const variacion = nodo.totalCostoLcy - nodo.presupuesto;
            // Sobre presupuesto (gasté más) = rojo; bajo presupuesto = verde.
            const color = variacion > 0 ? 'text-red-600' : 'text-green-600';
            const signo = variacion > 0 ? '+' : '';
            return (
              <span className={color}>
                {signo}
                {formatearCRC(variacion)}
              </span>
            );
          })()}
        </TableCell>
        <TableCell className="text-right text-xs tabular-nums">
          {nodo.totalQuantity.toLocaleString('es-CR', { maximumFractionDigits: 1 })}
        </TableCell>
      </TableRow>
      {abierto &&
        ordenarHermanos(nodo.hijos, orden, valoresNodo).map((hijo) => (
          <FilaArbol
            key={hijo.key}
            nodo={hijo}
            orden={orden}
            expandidas={expandidas}
            onToggle={onToggle}
            onAbrirObra={onAbrirObra}
          />
        ))}
    </>
  );
}
